import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, FilePlus2 } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import {
  ACCOUNTING_PERMISSIONS,
  ACCOUNTING_ROUTES,
  AccountingPostingKind,
  AccountingPostingStatus,
  type AccountingPostingRow,
} from './accountingApi';
import { deriveStatus, formatAge, referenceOf, toneForDerivedStatus, toneForKind } from './lib';
import PostingDetailDialog from './components/PostingDetailDialog';
import BuildPostingDialog from './components/BuildPostingDialog';

const KIND_FILTERS: readonly AccountingPostingKind[] = Object.values(AccountingPostingKind);
const STATUS_FILTERS: readonly AccountingPostingStatus[] = Object.values(AccountingPostingStatus);

/**
 * §accounting / ADR-0028 Part A — the postings ledger.
 *
 * This is the whole of what the UI-gap audit found missing: a venue could
 * install an Odoo (or any ACCOUNTING-category) connector through Integrations
 * and never see, build or reverse a single posting once it was in. The
 * install screen already exists (`Pages/Integrations`); this is the screen
 * that answers what happens after — did the sale reach the ledger, why not if
 * it didn't, and how to undo one that was wrong.
 *
 * Deliberately ONE screen, not a tabbed one like `Fiscal`. Fiscal earns its
 * "configuration" tab because a regime (taxpayer identity, tax/item code
 * mappings) is a whole aggregate of its own; accounting has none — the entire
 * "configuration" a venue does is the installation's own `config` JSON,
 * already editable from `Pages/Integrations/components/InstallationDetail.tsx`
 * (ADR-0028 §B4's reasoning for aggregator mappings applies here too: no new
 * table, no new screen, the installation already answers "which branch, what
 * does the ledger call it"). Building a second, mostly-empty configuration
 * tab here would just be a worse copy of a screen that already exists.
 */
export default function Accounting() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();

  const [kind, setKind] = useState<AccountingPostingKind | ''>('');
  const [status, setStatus] = useState<AccountingPostingStatus | ''>('');
  const [branchId, setBranchId] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [buildOpen, setBuildOpen] = useState(false);

  const canView = can(ACCOUNTING_PERMISSIONS.view);

  const params = useMemo(
    () => ({
      ...(kind ? { kind } : {}),
      ...(status ? { status } : {}),
      ...(branchId ? { branchId } : {}),
      limit: 100,
    }),
    [kind, status, branchId],
  );

  const postings = useApi<unknown>(canView ? ACCOUNTING_ROUTES.postings : null, params);
  const { rows } = rowsOf<AccountingPostingRow>(postings.data);

  const reload = useCallback(() => postings.reload(), [postings]);

  const clearFilters = () => {
    setKind('');
    setStatus('');
    setBranchId('');
  };
  const filtered = Boolean(kind || status || branchId);

  const money = (amount: string, currency: string) => formatCurrency(amount, currency, i18n.language);
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? id.slice(-6);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('accounting.title')}</h1>
          <p className="text-sm text-ink-muted">{t('accounting.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon as={RefreshCw} />
            {t('common.refresh')}
          </Button>
          {can(ACCOUNTING_PERMISSIONS.post) && (
            <Button size="sm" onClick={() => setBuildOpen(true)}>
              <Icon as={FilePlus2} />
              {t('accounting.buildForCheck.action')}
            </Button>
          )}
        </div>
      </div>

      {!canView ? (
        // The route already refuses anyone without `accounting.view` — reached
        // only if a looser route gate someday disagrees with this screen's own
        // key, which is worth seeing rather than rendering a table with nothing
        // in it.
        <ErrorState message={t('accounting.states.needsPermission')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('accounting.filters.kind')} controlId="accounting-kind">
              <Select value={kind} onChange={(e) => setKind(e.target.value as AccountingPostingKind | '')}>
                <option value="">{t('accounting.filters.allKinds')}</option>
                {KIND_FILTERS.map((k) => (
                  <option key={k} value={k}>
                    {t(`accounting.kind.${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('accounting.filters.status')} controlId="accounting-status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as AccountingPostingStatus | '')}>
                <option value="">{t('accounting.filters.allStatuses')}</option>
                {STATUS_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {t(`accounting.postingStatus.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {branches.length > 0 && (
              <Field label={t('accounting.filters.branch')} controlId="accounting-branch">
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('accounting.filters.allBranches')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>

          <TableWrap>
            {postings.loading ? (
              <LoadingState label={t('common.loading')} />
            ) : postings.error ? (
              <ErrorState message={postings.error} onRetry={reload} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={filtered ? t('accounting.states.noMatches') : t('accounting.states.emptyTitle')}
                hint={filtered ? t('accounting.states.noMatchesHint') : t('accounting.states.emptyHint')}
                action={
                  filtered ? (
                    <Button variant="outline" size="sm" onClick={clearFilters}>
                      {t('accounting.filters.clear')}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>{t('accounting.columns.kind')}</TH>
                    <TH>{t('accounting.columns.reference')}</TH>
                    <TH>{t('accounting.columns.branch')}</TH>
                    <TH className="text-end">{t('accounting.columns.amount')}</TH>
                    <TH>{t('accounting.columns.status')}</TH>
                    <TH>{t('accounting.columns.created')}</TH>
                    <TH />
                  </TR>
                </THead>
                <tbody>
                  {rows.length === 0 ? (
                    <TableEmpty colSpan={7}>{t('accounting.states.emptyTitle')}</TableEmpty>
                  ) : (
                    rows.map((posting) => {
                      const derived = deriveStatus(posting);
                      const ref = referenceOf(posting);
                      return (
                        <TR key={posting.id} className="cursor-pointer" onClick={() => setOpenId(posting.id)}>
                          <TD>
                            <Badge tone={toneForKind(posting.kind)}>{t(`accounting.kind.${posting.kind}`)}</Badge>
                          </TD>
                          <TD className="font-mono text-xs">
                            {ref.kind === 'check'
                              ? t('accounting.reference.check', { number: ref.label })
                              : t('accounting.reference.shift', { id: ref.label })}
                          </TD>
                          <TD className="text-sm">{branchName(posting.branchId)}</TD>
                          <TD className="text-end tabular-nums">{money(posting.amount, posting.currency)}</TD>
                          <TD>
                            <Badge tone={toneForDerivedStatus(derived)}>
                              {t(`accounting.derivedStatus.${derived}`)}
                            </Badge>
                          </TD>
                          <TD
                            className="text-xs text-ink-muted"
                            title={new Intl.DateTimeFormat(i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            }).format(new Date(posting.createdAt))}
                          >
                            {formatAge(posting.createdAt, i18n.language)}
                          </TD>
                          <TD>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenId(posting.id);
                              }}
                            >
                              {t('common.view')}
                            </Button>
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </TableWrap>
        </>
      )}

      <PostingDetailDialog postingId={openId} onClose={() => setOpenId(null)} onChanged={reload} />
      <BuildPostingDialog open={buildOpen} onClose={() => setBuildOpen(false)} onBuilt={reload} />
    </div>
  );
}
